import React from 'react';
import { fmtDate, getDaysInMonth, getMonday, WEEKDAY_SHORT, MONTH_NAMES } from './AttendancePage';

const CELL_COLORS = {
  present: 'bg-green-500 text-white',
  absent:  'bg-red-400 text-white',
  null:    'bg-gray-200 text-gray-500',
};
const CELL_LABELS = { present: 'N', absent: 'F', null: '–' };

// Build calendar weeks for a given year/month
const getCalendarWeeks = (year, month) => {
  const firstDay  = new Date(year, month, 1);
  const lastDay   = new Date(year, month + 1, 0);
  const start     = getMonday(firstDay);
  const weeks     = [];
  const current   = new Date(start);
  while (current <= lastDay || weeks.length < 6) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
    if (current > lastDay && weeks.length >= 4) break;
  }
  return weeks;
};

const AttendanceMonthView = ({
  participants,
  attendanceRecords,   // raw array of { participant_id, date, status } for counting
  getEffectiveStatus,
  year,
  month,
  mode,
  selected,
  onToggleSelect,
  onCellClick,
  onDayClick,
}) => {
  const daysInMonth = getDaysInMonth(year, month);
  const days        = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const calWeeks    = getCalendarWeeks(year, month);

  const getSummaryForDay = (day) => {
    const dateStr = fmtDate(new Date(year, month, day));
    const total   = participants.length;
    const present = attendanceRecords.filter(
      r => r.date?.startsWith(dateStr) && r.status === 'present'
    ).length;
    return { total, present };
  };

  return (
    <div data-testid="month-view" className="space-y-6">

      {/* ── Part 1: Calendar ── */}
      <div data-testid="month-calendar">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
          Kalender — {MONTH_NAMES[month]} {year}
        </h3>
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-7 bg-muted">
            {WEEKDAY_SHORT.map(d => (
              <div key={d} className="text-center text-xs font-bold py-2">{d}</div>
            ))}
          </div>
          {calWeeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-t">
              {week.map((d, di) => {
                const inMonth = d.getMonth() === month;
                const day     = d.getDate();
                const dateStr = fmtDate(d);
                const summary = inMonth ? getSummaryForDay(day) : null;
                return (
                  <div
                    key={di}
                    data-testid={inMonth ? `cal-day-${day}` : undefined}
                    onClick={() => inMonth && onDayClick && onDayClick(new Date(d))}
                    className={`min-h-[60px] p-1.5 border-r last:border-r-0 text-xs flex flex-col ${
                      inMonth
                        ? 'cursor-pointer hover:bg-muted/50 transition-colors'
                        : 'bg-muted/30 text-muted-foreground/50'
                    }`}
                  >
                    <span className={`font-bold ${inMonth ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {day}
                    </span>
                    {summary && summary.present > 0 && (
                      <span className="mt-auto bg-green-100 text-green-700 rounded px-1 text-[10px] font-bold">
                        {summary.present}/{summary.total}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Part 2: Matrix ── */}
      <div data-testid="month-matrix">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
          Månadsmatris
        </h3>
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse min-w-full" data-testid="month-matrix-table">
            <thead>
              <tr className="border-b">
                {mode === 'register' && <th className="w-8 sticky left-0 bg-white z-20" />}
                <th className="sticky left-0 bg-white z-10 text-left py-2 px-3 font-bold min-w-[140px]">
                  Namn
                </th>
                {days.map(d => (
                  <th key={d} className="py-2 px-1 text-center font-bold min-w-[28px]">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {participants.map(p => (
                <tr
                  key={p.id}
                  data-testid={`month-row-${p.id}`}
                  className={`border-b ${selected.has(p.id) ? 'bg-yellow-50' : 'hover:bg-muted/20'}`}
                >
                  {mode === 'register' && (
                    <td className="sticky left-0 bg-white z-10 py-1 px-2 text-center">
                      <input
                        type="checkbox"
                        data-testid={`month-select-${p.id}`}
                        checked={selected.has(p.id)}
                        onChange={() => onToggleSelect(p.id)}
                        className="rounded"
                        aria-label={`Välj ${p.name}`}
                      />
                    </td>
                  )}
                  <td className="sticky left-0 bg-white z-10 py-1 px-3 font-medium whitespace-nowrap">
                    {p.name}
                  </td>
                  {days.map(d => {
                    const dateStr = fmtDate(new Date(year, month, d));
                    const status  = getEffectiveStatus(p.id, dateStr);
                    return (
                      <td key={d} className="py-1 px-0.5 text-center">
                        <button
                          type="button"
                          data-testid={`month-cell-${p.id}-${d}`}
                          onClick={() => mode === 'register' && onCellClick(p.id, dateStr)}
                          disabled={mode === 'overview'}
                          className={`w-6 h-6 rounded text-[10px] font-bold transition-colors ${CELL_COLORS[status ?? 'null']} ${mode === 'register' ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                          aria-label={`${p.name} dag ${d}: ${status ?? 'Ej registrerad'}`}
                        >
                          {CELL_LABELS[status ?? 'null']}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {participants.length === 0 && (
                <tr>
                  <td colSpan={days.length + (mode === 'register' ? 2 : 1)} className="py-8 text-center text-muted-foreground">
                    Inga deltagare hittades.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceMonthView;
